import { useEffect, useState } from 'react';
import {
  FEATURES,
  FEATURE_DEFAULT,
  FEATURE_HINT,
  FEATURE_LABEL,
  type Feature,
  type FeatureRuleRow,
  type GroupRow,
} from 'shared';
import { api } from '../api';
import AdminList from '../AdminList';
import AdminFilters from '../AdminFilters';
import AdminSheet from '../AdminSheet';
import { alert, haptic } from '../telegram';
import { resetFeatures } from '../features';

/**
 * Управление функциями.
 *
 * Три уровня: всем, группе, человеку. Частное перебивает общее — так можно
 * открыть сырую функцию одному тестировщику, не выкатывая её на всех,
 * и наоборот, погасить её конкретной компании после жалобы.
 *
 * Список и разбор, как на остальных вкладках. Переключатель уехал из
 * строки в разбор — это лишнее нажатие на редкое действие, зато десять
 * функций стали читаться списком, а не десятью карточками, и видно, у
 * какой есть исключения.
 */
export default function AdminFeatures() {
  const [rules, setRules] = useState<FeatureRuleRow[]>([]);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<Feature | null>(null);
  const [query, setQuery] = useState('');
  /** Показывать только то, что отличается от значения по умолчанию. */
  const [changedOnly, setChangedOnly] = useState(false);

  async function load() {
    const [r, g] = await Promise.all([
      api.adminFeatures(),
      api.admin.groups().catch(() => ({ items: [] as GroupRow[] })),
    ]);
    setRules(r.items);
    setGroups(g.items);
  }

  useEffect(() => {
    void load();
  }, []);

  const globalOf = (f: Feature) =>
    rules.find((r) => r.feature === f && r.scope === 'GLOBAL');

  const stateOf = (f: Feature) => {
    const g = globalOf(f);
    return g ? g.enabled : FEATURE_DEFAULT[f];
  };

  const scopedOf = (f: Feature) =>
    rules.filter((r) => r.feature === f && r.scope !== 'GLOBAL');

  /** Тронута ли функция вообще: общее правило либо исключения. */
  const touched = (f: Feature) => Boolean(globalOf(f)) || scopedOf(f).length > 0;

  async function toggleGlobal(f: Feature, enabled: boolean) {
    setBusy(true);
    try {
      await api.setFeature({ feature: f, scope: 'GLOBAL', enabled });
      resetFeatures();
      await load();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addGroupRule(f: Feature, groupId: string, enabled: boolean) {
    if (!groupId) return;
    setBusy(true);
    try {
      await api.setFeature({ feature: f, scope: 'GROUP', groupId, enabled });
      resetFeatures();
      await load();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    await api.deleteFeature(id).catch(() => {});
    resetFeatures();
    await load();
  }

  const open = expanded;
  const q = query.trim().toLowerCase();
  const shown = FEATURES.filter((f) =>
    q ? `${FEATURE_LABEL[f]} ${FEATURE_HINT[f]}`.toLowerCase().includes(q) : true,
  ).filter((f) => (changedOnly ? touched(f) : true));

  return (
    <>
      <div className="meta" style={{ marginBottom: 'var(--s3)' }}>
        Правило для группы перебивает общее, персональное — оба. Без правил
        работает значение по умолчанию.
      </div>

      <AdminFilters
        active={changedOnly ? 1 : 0}
        search={
          <input
            placeholder="Название функции"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        }
      >
        <button
          type="button"
          className={changedOnly ? 'chip chip--on' : 'chip'}
          onClick={() => {
            haptic();
            setChangedOnly((v) => !v);
          }}
        >
          Только настроенные
        </button>
      </AdminFilters>

      <AdminList
        rows={shown}
        keyOf={(f) => f}
        empty="Ничего не найдено"
        action={{ label: 'Открыть', onClick: (f) => setExpanded(f) }}
        columns={[
          {
            title: 'Функция',
            cell: (f) => (
              <>
                <strong>{FEATURE_LABEL[f]}</strong>
                <span className="meta" style={{ display: 'block' }}>
                  {FEATURE_HINT[f]}
                </span>
              </>
            ),
          },
          {
            title: 'Всем',
            width: '140px',
            labelOnPhone: true,
            inline: true,
            cell: (f) => (
              <span className={stateOf(f) ? 'badge badge--verified' : 'badge badge--warn'}>
                {stateOf(f) ? 'включено' : 'выключено'}
              </span>
            ),
          },
          {
            title: 'Исключений',
            width: '130px',
            labelOnPhone: true,
            inline: true,
            cell: (f) => <span className="tabular">{scopedOf(f).length}</span>,
          },
          {
            title: 'Откуда значение',
            width: '170px',
            labelOnPhone: true,
            inline: true,
            // Различие важное: «по умолчанию» значит, что правила нет вовсе,
            // и значение изменится вместе с выкладкой, а не по решению
            // администратора.
            cell: (f) => (globalOf(f) ? 'задано' : 'по умолчанию'),
          },
        ]}
      />

      <AdminSheet
        open={open !== null}
        onClose={() => setExpanded(null)}
        title={open ? FEATURE_LABEL[open] : ''}
        subtitle={open && FEATURE_HINT[open]}
        actions={
          open && (
            <>
              <button className="secondary" onClick={() => setExpanded(null)}>
                Закрыть
              </button>
              <button disabled={busy} onClick={() => void toggleGlobal(open, !stateOf(open))}>
                {stateOf(open) ? 'Выключить всем' : 'Включить всем'}
              </button>
            </>
          )
        }
      >
        {open && (
          <>
            <div className="row">
              <span className="meta">Сейчас всем</span>
              <span className={stateOf(open) ? 'badge badge--verified' : 'badge badge--warn'}>
                {stateOf(open) ? 'включено' : 'выключено'}
              </span>
            </div>
            <div className="row">
              <span className="meta">Откуда значение</span>
              <span>{globalOf(open) ? 'задано администратором' : 'по умолчанию'}</span>
            </div>

            <div className="divider" />

            {scopedOf(open).length === 0 && (
              <div className="meta">Исключений нет — правило одно на всех.</div>
            )}

            {scopedOf(open).map((r) => (
              <div key={r.id} className="doc-row">
                <span className="meta">
                  {r.scope === 'GROUP' ? `Группа «${r.groupName}»` : r.userName}
                  {' — '}
                  {r.enabled ? 'включено' : 'выключено'}
                </span>
                <button className="file-del" onClick={() => void remove(r.id)}>
                  ×
                </button>
              </div>
            ))}

            {groups.length > 0 && (
              <div className="field" style={{ marginTop: 'var(--s3)' }}>
                <label>Исключение для группы</label>
                <select
                  defaultValue=""
                  onChange={(e) => {
                    const [groupId, mode] = e.target.value.split('|');
                    void addGroupRule(open, groupId ?? '', mode === 'on');
                    e.target.value = '';
                  }}
                >
                  <option value="">Выберите группу…</option>
                  {groups.map((gr) => (
                    <optgroup key={gr.id} label={gr.name}>
                      <option value={`${gr.id}|on`}>{gr.name}: включить</option>
                      <option value={`${gr.id}|off`}>{gr.name}: выключить</option>
                    </optgroup>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </AdminSheet>
    </>
  );
}
